import { useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { Pencil } from '@remarkable/ark-icons';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import { Button } from 'src/components';
import { COUNTRIES } from 'src/utils/countryList';

import { CardHeading } from './CardHeading';
import { CloudConnectBillingInfoModal } from './CloudConnectBillingInfoModal';

export const CloudConnectBillingInfoDetails = ({
  subscription,
}: {
  subscription: CloudConnectSubscription;
}) => {
  const address = subscription.address;
  const countryName = useMemo(() => {
    const country = COUNTRIES.find((c) => c.value === address.country);

    return country?.label ?? address.country;
  }, [address.country]);
  const cityLine = [
    address.city,
    address.state,
    address.postalCode,
    countryName,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex flex-col gap-16">
      <CardHeading>
        <Trans>Billing information</Trans>
      </CardHeading>

      <div className="flex flex-col justify-between gap-16 lm:flex-row">
        <div
          data-cy="billing-info-details"
          className="flex w-full min-w-0 grow flex-col gap-16 ls:flex-row lm:flex-col ll:flex-row"
        >
          <ul className="min-w-0 grow">
            <li>{subscription.name}</li>
            {address.line1 && <li>{address.line1}</li>}
            {address.line2 && <li>{address.line2}</li>}
            {cityLine && <li>{cityLine}</li>}
          </ul>
        </div>

        <CloudConnectBillingInfoModal
          subscription={subscription}
          trigger={
            <Button
              variant="tertiary"
              size="medium"
              className="w-full shrink-0 self-start whitespace-nowrap ls:w-fit"
              analytics={{
                text: 'Edit billing information',
                location: ComponentLocations.MANAGE_CONNECT.PAYMENT_PAGE,
                action: 'open edit billing information modal',
              }}
              data-cy="edit-billing-info-button"
            >
              <Pencil />
              <span>
                <Trans>Edit billing information</Trans>
              </span>
            </Button>
          }
        />
      </div>
    </div>
  );
};
